import React, { useState } from "react";
import {
  X,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Calendar,
  Calculator,
} from "lucide-react";
import type { ChartData as BurndownChartData } from "../../types/burndown.types";
import {
  calculateFundingAnalysis,
  type FundingAskPerspective,
} from "../../utils/chart/ask-calculator.utils";

interface ChartData extends BurndownChartData {}

interface AskCalculatorModalProps {
  data: Partial<ChartData>;
  currentCash: number;
  analysisDate?: Date;
  isOpen: boolean;
  onClose: () => void;
}

const AskCalculatorModal: React.FC<AskCalculatorModalProps> = ({
  data,
  currentCash,
  analysisDate,
  isOpen,
  onClose,
}) => {
  const [perspective, setPerspective] =
    useState<FundingAskPerspective>("initial_raise");

  const fundingAnalysis = React.useMemo(() => {
    const targetDate = analysisDate ?? data.startDate ?? new Date();
    return calculateFundingAnalysis(
      data as ChartData,
      targetDate,
      currentCash,
      { perspective },
    );
  }, [data, currentCash, analysisDate, perspective]);

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case "high":
      case "critical":
        return "#ef4444";
      case "medium":
      case "moderate":
        return "#f59e0b";
      default:
        return "#10b981";
    }
  };

  const runwayLabel =
    fundingAnalysis.perspective === "initial_raise"
      ? fundingAnalysis.netBurn <= 0
        ? "No opening raise required"
        : "Plan from $0 starting cash"
      : fundingAnalysis.currentRunwayMonths === Infinity
        ? "Self-Sustaining"
        : `${fundingAnalysis.currentRunwayMonths} months runway remaining`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        ></div>

        <div className="relative bg-surface rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          <div className="px-6 py-4 border-b bg-gradient-to-r from-brand/10 to-focus/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand/15 rounded-lg">
                  <Calculator className="w-5 h-5 text-brand" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">
                    Funding ASK Calculator
                  </h2>
                  <p className="text-sm text-white/65">
                    {fundingAnalysis.perspective === "initial_raise"
                      ? "Total raise needed from day zero"
                      : "Additional capital from your timeline position"}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/[0.06] rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white/45" />
              </button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="mb-6 flex rounded-lg border border-white/10 p-1">
              <button
                type="button"
                onClick={() => setPerspective("initial_raise")}
                className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  perspective === "initial_raise"
                    ? "bg-brand/20 text-emerald-100"
                    : "text-white/65 hover:text-white"
                }`}
              >
                Initial raise (day 0)
              </button>
              <button
                type="button"
                onClick={() => setPerspective("current_position")}
                className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  perspective === "current_position"
                    ? "bg-brand/20 text-emerald-100"
                    : "text-white/65 hover:text-white"
                }`}
              >
                From timeline date
              </button>
            </div>

            <div
              className="p-4 rounded-lg border-l-4 mb-6 bg-surface-elevated"
              style={{
                borderLeftColor: getUrgencyColor(
                  fundingAnalysis.fundingUrgency,
                ),
              }}
            >
              <div className="flex items-center gap-3 mb-3">
                <AlertTriangle
                  className="w-5 h-5"
                  style={{
                    color: getUrgencyColor(fundingAnalysis.fundingUrgency),
                  }}
                />
                <div>
                  <h3 className="font-semibold text-lg text-white">
                    {runwayLabel}
                  </h3>
                  <p className="text-sm text-white/65">
                    {fundingAnalysis.perspective === "initial_raise" ? (
                      <>
                        Modeled at startup launch · plan assumes $
                        {Math.round(
                          fundingAnalysis.configuredInitialFunding,
                        ).toLocaleString()}{" "}
                        initial funding in settings
                      </>
                    ) : (
                      <>
                        As of{" "}
                        {fundingAnalysis.analysisDate.toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          },
                        )}{" "}
                        · available cash $
                        {Math.round(fundingAnalysis.cashBalance).toLocaleString()}
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-white/65">Monthly Burn</p>
                  <p className="font-bold text-lg text-white">
                    ${Math.round(fundingAnalysis.monthlyBurn).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-white/65">Monthly Revenue</p>
                  <p className="font-bold text-lg text-white">
                    $
                    {Math.round(fundingAnalysis.monthlyRevenue).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-white/65" />
                  <div>
                    <p className="text-white/65">Optimal Timing</p>
                    <p className="font-bold text-white">
                      {fundingAnalysis.optimalTiming}
                    </p>
                  </div>
                </div>
              </div>

              {fundingAnalysis.perspective === "initial_raise" &&
                fundingAnalysis.configuredInitialFunding > 0 && (
                  <p className="mt-3 text-xs text-white/45">
                    Initial raise tab assumes $0 bank balance at launch. Your
                    startup settings already include $
                    {Math.round(
                      fundingAnalysis.configuredInitialFunding,
                    ).toLocaleString()}{" "}
                    — use &quot;From timeline date&quot; to see how much more
                    you need from today.
                  </p>
                )}
            </div>

            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-semibold text-white">
                {fundingAnalysis.perspective === "initial_raise"
                  ? "Opening Raise Scenarios"
                  : "Additional Funding Scenarios"}
              </h3>
              {fundingAnalysis.scenarios.map((scenario, index) => (
                <div
                  key={index}
                  className="border rounded-lg p-4 hover:shadow-glow transition-shadow"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white/90">
                        {scenario.name}
                      </h4>
                      <p className="text-sm text-white/65">
                        {scenario.description}
                      </p>
                    </div>
                    <div
                      className="px-3 py-1 rounded-full text-xs font-medium ml-4"
                      style={{
                        backgroundColor: getUrgencyColor(scenario.urgency),
                        color: "white",
                      }}
                    >
                      {scenario.urgency.toUpperCase()}
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-brand" />
                      <span className="font-bold text-2xl text-white">
                        ${scenario.amount.toLocaleString()}
                      </span>
                    </div>
                    <span className="text-sm text-white/45 bg-white/[0.06] px-2 py-1 rounded">
                      {scenario.months} months runway
                    </span>
                  </div>

                  <p className="text-sm text-white/75 bg-surface-elevated p-3 rounded italic">
                    {scenario.reasoning}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-brand/10 p-4 rounded-lg border border-brand/25">
              <h4 className="font-semibold text-emerald-100 mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Recommended Next Steps
              </h4>
              <ul className="text-sm text-emerald-200 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="font-bold">1.</span>
                  <span>
                    Use Initial raise to size your day-zero seed ask; use
                    Timeline to plan bridge rounds from today
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">2.</span>
                  <span>
                    Set initial funding to $0 in startup settings if you want
                    the plan to match a pure raise scenario
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">3.</span>
                  <span>
                    Prepare investor deck highlighting key traction metrics
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">4.</span>
                  <span>
                    Research investors aligned with your stage, sector, and
                    check size
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AskCalculatorModal;
